import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { CMS_PAGES_SLUG, STATUS, SWAL_SETTINGS, THEME_COLORS } from "../../../../../utils/Constants";
import {
  TrimText,
  formateDateWithMonth,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as pageServices from "../../../services/pages.services";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery, useQueryClient } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";
import NoRowsOverlay from "../../../common/NoRowsOverlay";

ModuleRegistry.registerModules([AllCommunityModule]);

const PageTable = (props) => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const queryClient = useQueryClient();

  const [searchData, setSearchData] = useState("");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);

  const location = useLocation();
  const isDevMode = new URLSearchParams(location.search).get('dev') === 'true';

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("sidebar_link_pages"), url: "" },
  ];

  // Helper function to return original slug
  const formatSlug = (slug) => {
    return slug || "N/A";
  };

  const formatShortDescription = (shortDescription) => {
    if (!shortDescription || shortDescription === "N/A") return shortDescription;
    return shortDescription
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Fetch all data at once for client-side pagination
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["cms-list"],
    queryFn: () => pageServices.List({
      limit: 1000,
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const changeStatus = (id, status) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        // OPTIMISTIC UPDATE - API se PEHELE
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(id);
          if (rowNode) {
            rowNode.setDataValue('status', status);  // 🔥 INSTANT UPDATE
          }
        }

        // API call AFTER optimistic update
        pageServices.Status({ status }, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
          } else {
            // API fail hua to REVERT back
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(id);
              if (rowNode) {
                rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
              }
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // Error pe bhi revert
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(id);
            if (rowNode) {
              rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
            }
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  // 🔥 OPTIMISTIC DELETE - NO REFRESH NEEDED
  const deleteRecord = (data) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const rowId = data.originalId;

        // 🔥 OPTIMISTIC UPDATE - INSTANTLY REMOVE ROW
        if (gridRef.current?.api) {
          gridRef.current.api.applyTransaction({
            remove: [{ originalId: rowId }]
          });
        }
          gridRef.current.api.refreshCells({ force: true });


        // API Call
        pageServices.Delete(rowId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
              timer: 1500,
              showConfirmButton: false
            });
          } else {
            // 🔥 REVERT ON FAILURE - ADD ROW BACK
            if (gridRef.current?.api && data) {
              gridRef.current.api.applyTransaction({
                add: [data]
              });
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // 🔥 REVERT ON ERROR - ADD ROW BACK
          if (gridRef.current?.api && data) {
            gridRef.current.api.applyTransaction({
              add: [data]
            });
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const colDefs = useMemo(() => [
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("label_page_title"),
      field: "title",
      flex: 1,
      minWidth: 400,
      maxWidth: 575,
      tooltipField: "title",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("label_slug"),
      field: "slug",
      minWidth: 150,
      tooltipField: "slug",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("label_short_description"),
      field: "shortDescription",
      flex: 1,
      minWidth: 100,
      maxWidth: 475,
      tooltipField: "shortDescription",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    ...(hasP("cms-status") ? [{
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 90,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => {
        const isActive = params.value === STATUS.ACTIVE;
        return (
          <label className={`custom-switch cp${!isActive ? " e_none" : ""}`}>
            <input
              type="checkbox"
              className="custom-switch-input cp"
              checked={isActive}
              onChange={() => {
                const newStatus = isActive ? STATUS.INACTIVE : STATUS.ACTIVE;
                changeStatus(params.data.originalId, newStatus);
              }}
            />
            <span className="custom-switch-indicator cp"></span>
          </label>
        );
      },
    }] : []),
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 150,
      maxWidth: 150,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
    },
    ...(hasP("cms-view") || hasP("cms-edit") || hasP("cms-delete") ? [{
      headerName: t("list_heading_action"),
      maxWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("cms-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => navigate(`/admin/cms/pages/view/${row.originalId}`)}
                  >
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}

              {hasP("cms-edit") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn edit"
                    onClick={() => navigate(`/admin/cms/pages/1/edit/${row.originalId}`)}
                  >
                    <i className="ri-pencil-fill"></i>
                  </button>
                </Whisper>
              )}

              {hasP("cms-delete") && ![
                CMS_PAGES_SLUG.PRIVACY_POLICY,
                CMS_PAGES_SLUG.TERMS_AND_CONDITION_WEB,
                CMS_PAGES_SLUG.GAME_RULES,
                CMS_PAGES_SLUG.PRIVACY_POLICY_WEB,
                CMS_PAGES_SLUG.PRIVACY_POLICY_WEB_LOWER,
                CMS_PAGES_SLUG.PRIVACY_POLICY_WEB_TYPO,
                CMS_PAGES_SLUG.PRIVACY_POLICY_WEB_TYPO2
              ].includes(row.originalSlug) && (
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                  >
                    <button
                      type="button"
                      className="btn delete"
                      onClick={() => deleteRecord(row)}
                    >
                      <i className="ri-delete-bin-6-fill"></i>
                    </button>
                  </Whisper>
                )}
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);

  // Row data preparation for AG Grid - S.No auto-calculates based on current filtered/sorted data
  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row) => ({
        originalId: row.id,
        title: row.title ? TrimText(row.title, 70) : "N/A",
        slug: formatSlug(row.slug),
        shortDescription: row.shortDescription ? TrimText(formatShortDescription(row.shortDescription), 70) : "N/A",
        status: row.status,
        createdAt: row.createdAt,
        originalSlug: row.slug,
      }))
      .filter((row) =>
        searchData
          ? row.title.toLowerCase().includes(searchData.toLowerCase()) ||
          row.slug.toLowerCase().includes(searchData.toLowerCase()) ||
          row.shortDescription.toLowerCase().includes(searchData.toLowerCase())
          : true
      );
  }, [data?.data?.list, searchData]); // 🔥 Fixed dependency

  // AG Grid Configuration Function
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    getRowId: (params) => params.data.originalId.toString(),
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
  }), [rowData, colDefs]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ borderBottom: "none" }}
          >
            <h6 className="main-content-label">{t("sidebar_link_cms")} {t("sidebar_link_pages")}</h6>
            <div className="d-flex align-items-center">
              <div style={{ width: 300 }} className="me-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchData}
                  onChange={(e) => setSearchData(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>
              {/* status */}
              <select
                className="form-select form-control me-3 cp"
                style={{ width: 100 }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (gridRef.current && gridRef.current.api) {
                    if (value && value.length) {
                      gridRef.current.api.setFilterModel({
                        status: {
                          type: "equals",
                          filter: value,
                        }
                      });
                    } else {
                      gridRef.current.api.setFilterModel(null);
                    }
                    gridRef.current.api.onFilterChanged();
                  }
                }}
              >
                <option value="">{t("list_heading_status")}</option>
                <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
              </select>

              <div className="me-3">
                <CustomProvider>
                  <DateRangePicker
                    style={{ width: "150px" }}
                    placeholder={t("input_placeholder_date")}
                    placement="bottomEnd"
                    onChange={(value) => {
                      if (!gridRef.current || !gridRef.current.api) return;

                      if (!value || value.length !== 2) {
                        gridRef.current.api.setFilterModel(null);
                        gridRef.current.api.onFilterChanged();
                        return;
                      }
                      const [start, end] = value;
                      gridRef.current.api.setFilterModel({
                        createdAt: {
                          type: "inRange",
                          dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                          dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                        }
                      });
                      gridRef.current.api.onFilterChanged();
                    }}
                  />
                </CustomProvider>
              </div>

              {isDevMode && hasP("cms-add") && (
                <button
                  className="btn ripple btn-main-primary signbtn"
                  onClick={() => navigate(`/admin/cms/pages/add`)}
                >
                  {t("btn_add_new")}
                </button>
              )}
            </div>
          </div>

          <div className="card-body">
            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PageTable;
